import isEmpty from 'lodash/isEmpty';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { useAdjustStateOnPropChange } from '~/hooks/useAdjustStateOnPropChange';
import IconSearch from '~/icons/icon-search.svg?react';
import SimpleTooltip from '../data-display/SimpleTooltip';
import TextField, { TextFieldProps } from '../inputs/TextField/TextField';

interface InlineSearchFilterProps<Filters> {
  autoFocus?: boolean;
  className?: string;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  name: keyof Filters;
  onChange: (name: keyof Filters, value: string) => void;
  value: string;
  hideLabel?: boolean;
  label?: string;
  placeholder?: string;
  size?: TextFieldProps['size'];
  onFocus?: TextFieldProps['onFocus'];
  onBlur?: TextFieldProps['onBlur'];
}

const InlineSearchFilter = <Filters,>({
  autoFocus,
  className,
  Icon = IconSearch,
  name,
  onChange,
  value,
  label,
  hideLabel,
  placeholder,
  size,
  onFocus,
  onBlur,
}: InlineSearchFilterProps<Filters>) => {
  const { t } = useTranslation('inputs');
  const [text, setText] = useState(value);
  const [showTooltip, setShowTooltip] = useState(false);

  if (showTooltip && text.length !== 1) {
    setShowTooltip(false);
  }

  const onSearchFieldChange = useCallback(
    (newValue: string) => {
      setShowTooltip(newValue.length === 1);

      if (newValue === value) return;

      if (newValue.length === 1) {
        onChange(name, '');
      } else {
        onChange(name, newValue);
      }
    },
    [name, onChange, value],
  );

  useAdjustStateOnPropChange(value, text, setText, (nextProp, _previousProp, state) => {
    // when value changes from outside we want to sync text except the case
    // when text have 1 char (we dont fetch for single char) and value change to '' (filters reset)
    if (isEmpty(nextProp) && state.length === 1) {
      return false;
    }
    return true;
  });

  const debouncedOnSearchFieldChange = useDebouncedCallback<(value: string) => void>(
    onSearchFieldChange,
    process.env.NODE_ENV === 'test' ? 5 : 500,
  );

  return (
    <SimpleTooltip
      className={className}
      arrow
      mode="div"
      title={t('search.min_length_help')}
      open={showTooltip}
      placement="bottom"
    >
      <TextField
        autoFocus={autoFocus}
        size={size}
        onFocus={onFocus}
        onBlur={onBlur}
        Icon={Icon}
        hideLabel={hideLabel}
        label={label ?? t('search.label')}
        placeholder={placeholder ?? label ?? t('search.label')}
        onChange={(value) => {
          setText(value);
          debouncedOnSearchFieldChange(value);
        }}
        onClear={() => {
          setText('');
          onSearchFieldChange('');
        }}
        value={text}
      />
    </SimpleTooltip>
  );
};

export default InlineSearchFilter;
